import React, { useState, useEffect, useRef } from "react";
import {
  Button, Card, Col, Container, Row, Spinner, Alert, Badge, ProgressBar, Modal
} from "react-bootstrap";
import { Box, Typography } from "@mui/material";
import {
  Warning, ArrowLeft, QrCode, CreditCard, Bank,
  Wallet, DeviceMobile, Receipt, House
} from "@phosphor-icons/react";
import { useLocation, useNavigate } from "react-router-dom";
import AppLayout from "../../layouts/AppLayout";
import usePaymentInitiator from "../../apis/PaymentInitiator";
import usePaymentStatus from "../../apis/PaymentStatusApi";

const Order = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableGateways, setAvailableGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [clickedButton, setClickedButton] = useState(null);

  // ✅ POLLING STATE
  const [isPolling, setIsPolling] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [canNavigateToReceipt, setCanNavigateToReceipt] = useState(false);

  // ✅ BACK BUTTON MODAL STATE
  const [showBackModal, setShowBackModal] = useState(false);
  const [backBlockEnabled, setBackBlockEnabled] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { state: orderData } = location;

  const { initiatePayment } = usePaymentInitiator();
  const { getPaymentStatus } = usePaymentStatus();
  const hasInitiatedPayment = useRef(false);
  const pollingIntervalRef = useRef(null);

  // ✅ GATEWAY ICON MAPPING
  const gatewayIcons = {
    PHONEPE: <DeviceMobile size={24} weight="bold" />,
    CASHFREE: <Wallet size={24} weight="bold" />,
    EASEBUZZ: <CreditCard size={24} weight="bold" />,
    RAZORPAY: <Bank size={24} weight="bold" />,
  };

  // ✅ BLOCK BACK BUTTON - SHOW MODAL
  useEffect(() => {
    if (!backBlockEnabled) return;

    window.history.pushState(null, null, window.location.href);
    
    const handleBack = () => {
      window.history.pushState(null, null, window.location.href);
      setShowBackModal(true);
    };

    window.addEventListener('popstate', handleBack);
    return () => window.removeEventListener('popstate', handleBack);
  }, [backBlockEnabled]);

  // ✅ HANDLE MODAL ACTIONS
  const handleGoToDashboard = () => {
    setShowBackModal(false);
    navigate('/dashboard', { replace: true });
  };

  const handleStay = () => {
    setShowBackModal(false);
  };

  // ✅ LOAD AVAILABLE GATEWAYS
  useEffect(() => {
    if (!orderData) {
      setError("Invalid navigation. Please select a loan from dashboard.");
      return;
    }

    const hasValidData =
      orderData.customer_id &&
      orderData.loan_number &&
      orderData.dueAmount;

    if (!hasValidData) {
      setError("Missing payment information. Please try again.");
      return;
    }

    const gateways = orderData.gateways || {};
    const enabledGateways = Object.values(gateways)
      .filter(gateway => gateway && typeof gateway === 'string' && gateway.trim() !== '')
      .map(gateway => gateway.toUpperCase().trim());

    setAvailableGateways(enabledGateways);
    setError(null);
  }, [orderData]);

  // ✅ POLLING FUNCTION
  const startPaymentStatusPolling = (orderId) => {
    setIsPolling(true);
    setCurrentOrderId(orderId);
    setPollingCount(0);

    const pollTimes = [10000, 20000, 30000];
    let currentPollIndex = 0;

    const checkStatus = () => {
      currentPollIndex++;
      setPollingCount(currentPollIndex);

      getPaymentStatus(orderId, (resp) => {
        if (resp?.is_success && resp.data) {
          const status = resp.data.payment_status?.toLowerCase();
          setPaymentStatus(status);

          if (status !== 'pending' && status !== 'initiated' && status !== 'processing') {
            clearInterval(pollingIntervalRef.current);
            setIsPolling(false);
            setCanNavigateToReceipt(true);
            setLoading(false);
            return;
          }

          if (currentPollIndex >= 3) {
            clearInterval(pollingIntervalRef.current);
            setIsPolling(false);
            setCanNavigateToReceipt(true);
            setLoading(false);
          }
        } else {
          if (currentPollIndex >= 3) {
            clearInterval(pollingIntervalRef.current);
            setIsPolling(false);
            setCanNavigateToReceipt(true);
            setLoading(false);
          }
        }
      });
    };

    setTimeout(() => checkStatus(), pollTimes[0]);
    setTimeout(() => checkStatus(), pollTimes[1]);
    setTimeout(() => checkStatus(), pollTimes[2]);
  };

  // ✅ CLEANUP POLLING
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // ✅ HANDLE PAYMENT INITIATION
  const handlePayment = (gateway, isQR = false) => {
    if (hasInitiatedPayment.current) {
      return;
    }

    if (!orderData?.loan_number || !orderData?.dueAmount) {
      setError("Missing payment information");
      return;
    }

    // ✅ ENABLE BACK BUTTON BLOCK
    setBackBlockEnabled(true);

    setLoading(true);
    setError(null);
    setSelectedGateway(gateway);
    setClickedButton(isQR ? 'QR' : gateway);
    hasInitiatedPayment.current = true;

    initiatePayment(
      {
        loan_number: orderData.loan_number,
        amount: orderData.dueAmount?.toString(),
        customer_id: orderData.customer_id?.toString(),
        module_code: orderData.moduleCode || "DEFAULT",
        gateway: gateway
      },
      (response) => {
        if (response?.is_success && response.data) {
          const { order_id, payment_urls } = response.data;

          if (isQR) {
            navigate("/ShowQr", {
              state: {
                orderId: order_id,
                upiString: payment_urls,
                amount: orderData.dueAmount,
                loanNumber: orderData.loan_number,
                gateway: gateway
              },
              replace: true
            });
          } else {
            if (payment_urls && payment_urls.startsWith("upi://")) {
              window.location.href = payment_urls;

              setTimeout(() => {
                startPaymentStatusPolling(order_id);
              }, 2000);
            } else {
              setError("Invalid payment URL received");
              setLoading(false);
              hasInitiatedPayment.current = false;
              setClickedButton(null);
              setBackBlockEnabled(false);
            }
          }
        } else {
          setError(response?.message || "Payment initiation failed");
          setLoading(false);
          hasInitiatedPayment.current = false;
          setSelectedGateway(null);
          setClickedButton(null);
          setBackBlockEnabled(false);
        }
      }
    );
  };

  // ✅ NAVIGATE TO RECEIPT
  const handleNavigateToReceipt = () => {
    navigate("/PaymentReceipt", {
      state: {
        orderId: currentOrderId,
        paymentData: {
          payment_status: paymentStatus,
          amount: orderData.dueAmount,
          loan_number: orderData.loan_number,
          gateway: selectedGateway
        }
      },
      replace: true
    });
  };

  // ✅ GET ICON FOR GATEWAY
  const getGatewayIcon = (gateway) => {
    return gatewayIcons[gateway] || <CreditCard size={24} weight="bold" />;
  };

  // ✅ RENDER GATEWAY BUTTONS
  const renderGatewayButtons = () => {
    if (availableGateways.length === 0) {
      return (
        <Alert variant="warning">
          <Warning size={20} className="me-2" />
          No payment gateways available. Please contact support.
        </Alert>
      );
    }

    const hasPhonePe = availableGateways.includes("PHONEPE");

    return (
      <div className="d-grid gap-2">
        {availableGateways.map((gateway) => (
          <Button
            key={gateway}
            variant={clickedButton === gateway ? "success" : "outline-primary"}
            size="sm"
            className="py-2 py-md-2"
            onClick={() => handlePayment(gateway, false)}
            disabled={loading || hasInitiatedPayment.current || isPolling}
          >
            {clickedButton === gateway && loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                {getGatewayIcon(gateway)}
                <span className="ms-2">Pay via {gateway}</span>
              </>
            )}
          </Button>
        ))}

        {hasPhonePe && (
          <Button
            variant={clickedButton === 'QR' ? "success" : "outline-primary"}
            size="sm"
            className="py-2 py-md-2 position-relative"
            onClick={() => handlePayment("PHONEPE", true)}
            disabled={loading || hasInitiatedPayment.current || isPolling}
          >
            {clickedButton === 'QR' && loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <QrCode size={24} className="me-2" weight="bold" />
                Scan QR Code
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  // ✅ NO ORDER DATA SCREEN
  if (!orderData) {
    return (
      <AppLayout>
        <Container className="px-3 mt-4">
          <Row className="justify-content-center">
            <Col xs={12} md={8} lg={6}>
              <Card className="shadow-lg border-0">
                <Card.Body className="p-4 text-center">
                  <Warning size={48} className="text-warning mb-3" />
                  <h4 className="mb-3">No Order Data</h4>
                  <p className="text-muted mb-4">
                    Please select a loan from the dashboard to proceed with payment.
                  </p>
                  <Button variant="primary" onClick={() => navigate("/dashboard/")}>
                    <ArrowLeft size={16} className="me-2" />
                    Go to Dashboard
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Container className="px-3 mt-4">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
          mt={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Payment Portal
          </Typography>
        </Box>

        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <Card className="shadow-lg border-0">
              <Card.Body className="p-4">

                {/* ===== HEADER ===== */}
                <div className="text-center mb-4">
                 <Typography 
  variant="h3" 
  component="h2" 
  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}  
  className="mb-3"
>
  Select Payment Method
</Typography>
                  {/* {orderData && (
                    <>
                      <h4 className="text-primary mb-2">
                        ₹{orderData.dueAmount?.toLocaleString('en-IN')}
                      </h4>
                      <p className="text-muted mb-0">{orderData.loan_number}</p>
                      {availableGateways.length > 0 && !loading && !isPolling && (
                        <Badge bg="info" className="mt-2">
                          Select a Payment Option
                        </Badge>
                      )}
                      {loading && !isPolling && (
                        <Badge bg="warning" className="mt-2">
                          Processing Payment...
                        </Badge>
                      )}
                      {isPolling && (
                        <Badge bg="info" className="mt-2">
                          Checking Payment Status...
                        </Badge>
                      )}
                    </>
                  )} */}
                </div>

                {/* ===== LOADING STATE ===== */}
                {loading && !isPolling && (
                  <Alert variant="white" className="text-center">
                    <Spinner size="sm" className="me-2" />
                    Initiating payment via {selectedGateway}...
                    <div className="mt-2">
                      <small className="text-muted">Please wait, do not refresh or go back</small>
                    </div>
                  </Alert>
                )}

                {/* ===== POLLING STATUS ===== */}
                {isPolling && (
                  <Alert variant="white" className="text-center">
                    <Spinner size="sm" className="me-2" />
                    <div className="mb-2">
                      <strong>Checking payment status...</strong>
                    </div>
                    <ProgressBar
                      now={(pollingCount / 3) * 100}
                      animated
                      className="mb-2"
                    />
                    {paymentStatus && (
                      <div>
                        <small>Current Status: <strong>{paymentStatus.toUpperCase()}</strong></small>
                      </div>
                    )}
                    <div className="mt-2">
                      <small className="text-muted">
                        Please complete payment in your UPI app
                      </small>
                    </div>
                  </Alert>
                )}

                {/* ===== NAVIGATE TO RECEIPT BUTTON ===== */}
                {canNavigateToReceipt && (
                  <Card className="border-success mb-3">
                    <Card.Body className="p-4 text-center">
                      <div className="mb-3">
                        <div
                          className="rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{
                            width: '64px',
                            height: '64px',
                            backgroundColor: '#d4edda',
                            border: '3px solid #009be2'
                          }}
                        >
                          <Receipt size={32} weight="fill" className="text-info" />
                        </div>
                      </div>

                      <h5 className="mb-2 text-info">Status</h5>

                     <Alert variant="info" className="mb-4">
  For your updated payment status click View Receipt Button.
</Alert>
   {currentOrderId && (
                        <div className="mt-2 md-2">
                          <small className="text-muted">
                            Order ID: <strong>{currentOrderId}</strong>
                          </small>
                        </div>
                      )}
                      {paymentStatus && (
                        <Badge
                          bg={paymentStatus === 'success' ? 'success' : paymentStatus === 'pending' ? 'warning' : 'secondary'}
                          className="mb-3 px-3 py-2"
                        >
                          Status: {paymentStatus.toUpperCase()}
                        </Badge>
                      )}

                      <div className="d-grid gap-2">
                        <Button
                          variant="info"
                          onClick={handleNavigateToReceipt}
                          className="w-100 py-2"
                          style={{
                            fontWeight: '600',
                            fontSize: '15px',
                            borderRadius: '8px'
                          }}
                        >
                          <Receipt size={20} className="me-2" weight="bold" />
                          View Receipt
                        </Button>

                        <Button
                          variant="outline-secondary"
                          onClick={() => navigate("/dashboard/")}
                        >
                          <ArrowLeft size={16} className="me-2" />
                          Home
                        </Button>
                      </div>

                   
                    </Card.Body>
                  </Card>
                )}

                {/* ===== ERROR STATE ===== */}
                {error && (
                  <Alert variant="danger">
                    <Warning size={20} className="me-2" />
                    {error}
                  </Alert>
                )}

                {/* ===== GATEWAY BUTTONS ===== */}
                {!canNavigateToReceipt && renderGatewayButtons()}

                {/* ===== CANCEL BUTTON ===== */}
                {!canNavigateToReceipt && (
                  <div className="text-center mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate("/dashboard/")}
                      disabled={loading || hasInitiatedPayment.current || isPolling}
                    >
                      <ArrowLeft size={16} className="me-2" />
                      {loading || isPolling ? "Please Wait..." : "Cancel"}
                    </Button>
                  </div>
                )}

              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ✅ BACK BUTTON WARNING MODAL */}
      <Modal show={showBackModal} onHide={handleStay} centered backdrop="static">
        <Modal.Body className="text-center p-4">
          <Warning size={48} className="text-warning mb-3" />
          <h5 className="mb-3">Payment in Progress!</h5>
          <p className="text-muted mb-4">
            Payment is being processed. Going back is not allowed.
            <br />
            Please use the buttons below.
          </p>
          <div className="d-grid gap-2">
            <Button 
              variant="primary" 
              size="lg"
              onClick={handleGoToDashboard}
            >
              <House size={20} className="me-2" />
              Go to Home
            </Button>
            <Button 
              variant="outline-secondary"
              onClick={handleStay}
            >
              Stay
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </AppLayout>
  );
};

export default Order;
